"use strict";
// (C) 2022 GoodData Corporation
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDashboardScheduledEmails = void 0;
var tslib_1 = require("tslib");
var react_1 = require("react");
var sdk_ui_kit_1 = require("@gooddata/sdk-ui-kit");
var DashboardStoreProvider_1 = require("./DashboardStoreProvider");
var store_1 = require("../store");
var locales_1 = require("../../locales");
/**
 * Hook that handles schedule emailing dialogs.
 *
 * @alpha
 */
exports.useDashboardScheduledEmails = function () {
    var _a;
    var _b = sdk_ui_kit_1.useToastMessage(), addSuccess = _b.addSuccess, addError = _b.addError;
    var isScheduleEmailingDialogOpen = DashboardStoreProvider_1.useDashboardSelector(store_1.selectIsScheduleEmailDialogOpen);
    var isScheduleEmailingManagementDialogOpen = DashboardStoreProvider_1.useDashboardSelector(store_1.selectIsScheduleEmailManagementDialogOpen);
    var dispatch = DashboardStoreProvider_1.useDashboardDispatch();
    var dashboardRef = DashboardStoreProvider_1.useDashboardSelector(store_1.selectDashboardRef);
    var enableInsightExportScheduling = DashboardStoreProvider_1.useDashboardSelector(store_1.selectEnableInsightExportScheduling);
    var isReadOnly = DashboardStoreProvider_1.useDashboardSelector(store_1.selectIsReadOnly);
    var isInViewMode = DashboardStoreProvider_1.useDashboardSelector(store_1.selectIsInViewMode);
    var canCreateScheduledMail = DashboardStoreProvider_1.useDashboardSelector(store_1.selectCanCreateScheduledMail);
    var isScheduledEmailingEnabled = DashboardStoreProvider_1.useDashboardSelector(store_1.selectEnableKPIDashboardSchedule);
    var menuButtonItemsVisibility = DashboardStoreProvider_1.useDashboardSelector(store_1.selectMenuButtonItemsVisibility);
    var openScheduleEmailingDialog = function () { return dispatch(store_1.uiActions.openScheduleEmailDialog()); };
    var closeScheduleEmailingDialog = function () { return dispatch(store_1.uiActions.closeScheduleEmailDialog()); };
    var openScheduleEmailingManagementDialog = function () {
        return enableInsightExportScheduling && dispatch(store_1.uiActions.openScheduleEmailManagementDialog());
    };
    var closeScheduleEmailingManagementDialog = function () {
        return enableInsightExportScheduling && dispatch(store_1.uiActions.closeScheduleEmailManagementDialog());
    };
    var setScheduledEmailDefaultAttachment = function (attachmentRef) {
        return enableInsightExportScheduling &&
            dispatch(store_1.uiActions.setScheduleEmailDialogDefaultAttachment(attachmentRef));
    };
    var resetScheduledEmailDefaultAttachment = function () {
        return enableInsightExportScheduling && dispatch(store_1.uiActions.resetScheduleEmailDialogDefaultAttachment());
    };
    var _c = tslib_1.__read(react_1.useState(), 2), scheduledEmailToEdit = _c[0], setScheduledEmailToEdit = _c[1];
    var _d = tslib_1.__read(react_1.useState([]), 2), users = _d[0], setUsers = _d[1];
    var isScheduledEmailingVisible = isInViewMode &&
        !isReadOnly &&
        canCreateScheduledMail &&
        isScheduledEmailingEnabled &&
        ((_a = menuButtonItemsVisibility.scheduleEmailButton) !== null && _a !== void 0 ? _a : true);
    /*
     * exports and scheduling are not available when rendering a dashboard that is not persisted.
     * this can happen when a new dashboard is created and is being edited.
     *
     * the setup of menu items available in the menu needs to reflect this.
     */
    var defaultOnScheduleEmailing = react_1.useCallback(function () {
        if (!dashboardRef) {
            return;
        }
        if (enableInsightExportScheduling) {
            openScheduleEmailingManagementDialog();
        }
        else {
            openScheduleEmailingDialog();
        }
    }, [dashboardRef, enableInsightExportScheduling]);
    var onScheduleEmailingOpen = react_1.useCallback(function (attachmentRef) {
        openScheduleEmailingDialog();
        attachmentRef && setScheduledEmailDefaultAttachment(attachmentRef);
    }, []);
    var onScheduleEmailingCreateError = react_1.useCallback(function () {
        closeScheduleEmailingDialog();
        addError(locales_1.messages.scheduleEmailSubmitError);
    }, []);
    var onScheduleEmailingCreateSuccess = react_1.useCallback(function () {
        closeScheduleEmailingDialog();
        addSuccess(locales_1.messages.scheduleEmailSubmitSuccess);
        resetScheduledEmailDefaultAttachment();
    }, []);
    var onScheduleEmailingSaveError = react_1.useCallback(function () {
        closeScheduleEmailingDialog();
        addError(locales_1.messages.scheduleEmailSaveError);
        setScheduledEmailToEdit(undefined);
    }, []);
    var onScheduleEmailingSaveSuccess = react_1.useCallback(function () {
        closeScheduleEmailingDialog();
        openScheduleEmailingManagementDialog();
        addSuccess(locales_1.messages.scheduleEmailSaveSuccess);
        setScheduledEmailToEdit(undefined);
    }, []);
    var onScheduleEmailingCancel = react_1.useCallback(function () {
        closeScheduleEmailingDialog();
        openScheduleEmailingManagementDialog();
        resetScheduledEmailDefaultAttachment();
        setScheduledEmailToEdit(undefined);
    }, []);
    var onScheduleEmailingManagementDeleteSuccess = react_1.useCallback(function () {
        addSuccess(locales_1.messages.scheduleEmailDeleteSuccess);
    }, []);
    var onScheduleEmailingManagementAdd = react_1.useCallback(function () {
        closeScheduleEmailingManagementDialog();
        openScheduleEmailingDialog();
    }, []);
    var onScheduleEmailingManagementEdit = react_1.useCallback(function (schedule, users) {
        closeScheduleEmailingManagementDialog();
        setScheduledEmailToEdit(schedule);
        setUsers(users);
        openScheduleEmailingDialog();
    }, []);
    var onScheduleEmailingManagementClose = react_1.useCallback(function () {
        closeScheduleEmailingManagementDialog();
    }, []);
    var onScheduleEmailingManagementLoadingError = react_1.useCallback(function () {
        closeScheduleEmailingManagementDialog();
        addError(locales_1.messages.scheduleManagementLoadError);
    }, []);
    var onScheduleEmailingManagementDeleteError = react_1.useCallback(function () {
        closeScheduleEmailingManagementDialog();
        addError(locales_1.messages.scheduleManagementDeleteError);
    }, []);
    return {
        isScheduledEmailingVisible: isScheduledEmailingVisible,
        enableInsightExportScheduling: enableInsightExportScheduling,
        defaultOnScheduleEmailing: defaultOnScheduleEmailing,
        isScheduleEmailingDialogOpen: isScheduleEmailingDialogOpen,
        isScheduleEmailingManagementDialogOpen: isScheduleEmailingManagementDialogOpen,
        onScheduleEmailingOpen: onScheduleEmailingOpen,
        onScheduleEmailingManagementEdit: onScheduleEmailingManagementEdit,
        scheduledEmailToEdit: scheduledEmailToEdit,
        users: users,
        onScheduleEmailingCancel: onScheduleEmailingCancel,
        onScheduleEmailingCreateError: onScheduleEmailingCreateError,
        onScheduleEmailingCreateSuccess: onScheduleEmailingCreateSuccess,
        onScheduleEmailingSaveError: onScheduleEmailingSaveError,
        onScheduleEmailingSaveSuccess: onScheduleEmailingSaveSuccess,
        onScheduleEmailingManagementAdd: onScheduleEmailingManagementAdd,
        onScheduleEmailingManagementClose: onScheduleEmailingManagementClose,
        onScheduleEmailingManagementLoadingError: onScheduleEmailingManagementLoadingError,
        onScheduleEmailingManagementDeleteSuccess: onScheduleEmailingManagementDeleteSuccess,
        onScheduleEmailingManagementDeleteError: onScheduleEmailingManagementDeleteError,
    };
};
//# sourceMappingURL=useDashboardScheduledEmails.js.map